import { parseHtml, useMessageChannelContext } from '@healthhub/react-ui';
import type { SerializedError } from '@reduxjs/toolkit';
import { Modal } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { showCaregiverPermissionErrorModal, showErrorModal } from '@shared/components';
import { FIN_NRIC_CODE } from '@shared/constants/constants';
import { useLayoutViewContext } from '@shared/context';
import useIsAuthenticated from '@shared/hooks/useIsAuthenticated';
import useNavigateStandardHealthProfile from '@shared/hooks/useNavigateStandardHealthProfile';
import { useLazyCheckCaregiverPermissionQuery } from '@shared/services/caregivers';
import type { RelatedPersonTypeType } from '@shared/services/caregivers/types/getRelatedPeople.types';
import { MESSAGE_ACTION } from '@shared/utils/bridge';

interface Props {
  defaultErrorMessage?: string;
  generateBtnText: (errorCode?: string) => string;
  modalBtnText?: string;
  path?: string;
}

const useHandleCheckCaregiverPermission = ({
  defaultErrorMessage = "You don't have permission to do the action",
  generateBtnText,
  modalBtnText,
  path,
}: Props) => {
  const { isMobileView, isSharedServiceView, isStandardView } = useLayoutViewContext();
  const { sendMessageToNative } = useMessageChannelContext();

  const { isAuthenticated } = useIsAuthenticated();
  const routerNavigate = useNavigate();
  const { isStandardHealthProfile } = useNavigateStandardHealthProfile();

  const { t } = useTranslation('home');
  const [hasFINError, setHasFINError] = useState(false);

  const onCloseModal = useCallback(() => {
    Modal.destroyAll();
    setHasFINError(false);
    if ((isMobileView || isSharedServiceView || isStandardView) && !isStandardHealthProfile) {
      sendMessageToNative(MESSAGE_ACTION.back());
      return;
    }

    if (path) routerNavigate(path);
  }, [
    isStandardHealthProfile,
    path,
    routerNavigate,
    isMobileView,
    isSharedServiceView,
    isStandardView,
    sendMessageToNative,
  ]);

  const [checkCaregiverPermission, { isLoading }] = useLazyCheckCaregiverPermissionQuery();

  const checkPermission = useCallback(
    async (relationshipType: RelatedPersonTypeType, shouldShowErrorModal = true) => {
      if (!isAuthenticated) {
        return true;
      }

      try {
        await checkCaregiverPermission({
          RelationshipType: relationshipType,
        }).unwrap();

        return true;
      } catch (error) {
        const err = error as SerializedError;

        const isFinError = err?.code === FIN_NRIC_CODE;
        setHasFINError(isFinError);

        if (!shouldShowErrorModal) {
          return true;
        }

        if (isFinError) {
          showCaregiverPermissionErrorModal({ btnText: t('U0008.buttonText'), title: t('U0008.title') });
          return false;
        }

        showErrorModal({
          content: err?.message ? parseHtml(err?.message ?? '') : defaultErrorMessage,
          errorCode: err?.code,
          hideTitle: true,
          btnText: modalBtnText ?? t(generateBtnText(err?.code)),
          onClose: onCloseModal,
        });
        return false;
      }
    },
    [checkCaregiverPermission, defaultErrorMessage, modalBtnText, generateBtnText, isAuthenticated, onCloseModal, t]
  );

  return {
    checkPermission,
    hasFINError,
    isLoading,
  };
};

export default useHandleCheckCaregiverPermission;
